import { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HeaderContainer,
  HeaderLeft,
  DesktopNav,
  NavGroup,
  Divider,
  StyledNavLink,
  ThemeSelect,
  MobileMenuButton,
  MobileOverlay,
  MobileDrawer,
  DrawerHeader,
  DrawerNavList,
  DrawerSectionTitle,
  DrawerControls,
  DrawerControlGroup,
  DrawerLabel,
  DrawerAccount,
  DropdownWrapper,
  DropdownMenu,
  UserName,
  AdminNavLink,
  TruncatedName,
  LogoutButton,
  HeaderRight,
  NavText,
} from "./Navigation.styles";
import { Logo } from "../ui/Logo/Logo";
import { APP_CONFIG } from "../../config";
import { appRoutes } from "../../config/routes";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeContext } from "../../contexts/ThemeContext";
import { ThemeStyle, ThemeMode } from "../../styles/theme";
import { useLanguageSwitch } from "../../hooks/useLanguageSwitch";
import { Link } from "../../components/ui/Link/Link";
import { ThemeToggle } from "../ui/ThemeToggle/ThemeToggle";
import {
  useMyWorkspaces,
  useSwitchWorkspace,
} from "../../hooks/queries/useWorkspaces";
import { StyleToggle } from "../ui/StyleToggle/StyleToggle";
import { LanguageToggle } from "../ui/LanguageToggle/LanguageToggle";
import { NotificationBell } from "../ui/NotificationBell/NotificationBell";
import { SupportWidget } from "../SupportWidget/SupportWidget";
import { AppearanceSettings } from "../ui/AppearanceSettings/AppearanceSettings";
import {
  SettingGroup,
  SettingLabel,
  SettingSelect,
} from "../ui/AppearanceSettings/AppearanceSettings.styles";
import { formatNumber } from "../../utils/formatters";

const MenuIcon = () => (
  <svg
    width="1.5rem"
    height="1.5rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const CloseIcon = () => (
  <svg
    width="1.5rem"
    height="1.5rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const LogoutIcon = () => (
  <svg
    width="1rem"
    height="1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const SettingsIcon = () => (
  <svg
    width="1rem"
    height="1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="1.1rem"
    height="1.1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const ShieldIcon = () => (
  <svg
    width="1.1rem"
    height="1.1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const AnalyticsIcon = () => (
  <svg
    width="1rem"
    height="1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <line x1="18" y1="20" x2="18" y2="10"></line>
    <line x1="12" y1="20" x2="12" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="14"></line>
  </svg>
);

const CampaignsIcon = () => (
  <svg
    width="1rem"
    height="1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
    <polyline points="2 17 12 22 22 17"></polyline>
    <polyline points="2 12 12 17 22 12"></polyline>
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="1.2rem"
    height="1.2rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, marginLeft: "-0.25rem", color: "#a1a1aa" }}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const WorkspaceIcon = () => (
  <svg
    width="1.1rem"
    height="1.1rem"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

export const Navigation = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || localStorage.getItem("i18nextLng") || "en";
  const { user, logout } = useAuth();
  const { data: myWorkspaces } = useMyWorkspaces();
  const { mutate: switchWorkspace } = useSwitchWorkspace();
  const activeWorkspace = myWorkspaces?.find((ws) => ws.active);
  const { themeStyle, themeMode, setThemeStyle, setThemeMode } =
    useThemeContext();
  const { t, i18n } = useTranslation();
  const { switchLanguage } = useLanguageSwitch();

  const mainNav = appRoutes.filter((route) => route.section === "main");
  const campaignsNav = appRoutes.filter((route) => route.section === "campaigns");
  const assetsNav = appRoutes.filter((route) => route.section === "assets");
  const analyticsNav = appRoutes.filter(
    (route) => route.section === "analytics",
  );
  const settingsNav = appRoutes.filter((route) => route.section === "settings");

  const checkIsActive = (path: string) => {
    if (!path)
      return (
        location.pathname === `/${currentLang}` ||
        location.pathname === `/${currentLang}/`
      );
    return (
      location.pathname === `/${currentLang}/${path}` ||
      location.pathname.startsWith(`/${currentLang}/${path}/`)
    );
  };

  const isSettingsActive = settingsNav.some((route) =>
    checkIsActive(route.path),
  );

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Non-commercial demo mode: no balance banners */}
      <HeaderContainer>
        <HeaderLeft>
          <Logo />

          <DesktopNav>
            <NavGroup>
              {mainNav.map((item) => (
                <Link key={item.path} to={item.path}>
                  <StyledNavLink isActive={checkIsActive(item.path)}>
                    {item.i18nKey ? t(item.i18nKey) : item.label}
                  </StyledNavLink>
                </Link>
              ))}

              {campaignsNav.length > 0 && (
                <DropdownWrapper>
                  <StyledNavLink
                    isActive={campaignsNav.some((route) =>
                      checkIsActive(route.path),
                    )}
                  >
                    <CampaignsIcon />
                    {t("nav:campaigns", "Campaigns")}
                  </StyledNavLink>

                  <DropdownMenu $align="left">
                    <div>
                      {campaignsNav.map((item) => (
                        <Link key={item.path} to={item.path}>
                          <StyledNavLink isActive={checkIsActive(item.path)}>
                            {item.i18nKey ? t(item.i18nKey) : item.label}
                          </StyledNavLink>
                        </Link>
                      ))}
                    </div>
                  </DropdownMenu>
                </DropdownWrapper>
              )}

              {assetsNav.length > 0 && (
                <DropdownWrapper>
                  <StyledNavLink
                    isActive={assetsNav.some((route) =>
                      checkIsActive(route.path),
                    )}
                  >
                    <ShieldIcon />{" "}
                    {/* You can replace with AssetsIcon if available */}
                    {t("nav:traffic_assets", "Traffic Assets")}
                  </StyledNavLink>

                  <DropdownMenu $align="left">
                    <div>
                      {assetsNav.map((item) => (
                        <Link key={item.path} to={item.path}>
                          <StyledNavLink isActive={checkIsActive(item.path)}>
                            {item.i18nKey ? t(item.i18nKey) : item.label}
                          </StyledNavLink>
                        </Link>
                      ))}
                    </div>
                  </DropdownMenu>
                </DropdownWrapper>
              )}

              {analyticsNav.length > 0 && (
                <DropdownWrapper>
                  <StyledNavLink
                    isActive={analyticsNav.some((route) =>
                      checkIsActive(route.path),
                    )}
                  >
                    <AnalyticsIcon />
                    {t("nav:analytics", "Analytics")}
                  </StyledNavLink>

                  <DropdownMenu $align="left">
                    <div>
                      {analyticsNav.map((item) => (
                        <Link key={item.path} to={item.path}>
                          <StyledNavLink isActive={checkIsActive(item.path)}>
                            {item.i18nKey ? t(item.i18nKey) : item.label}
                          </StyledNavLink>
                        </Link>
                      ))}
                    </div>
                  </DropdownMenu>
                </DropdownWrapper>
              )}
            </NavGroup>
          </DesktopNav>
        </HeaderLeft>

        <HeaderRight>
          <NavGroup>
            {myWorkspaces && myWorkspaces.length > 0 && (
              <>
                <DropdownWrapper>
                  <StyledNavLink>
                    <WorkspaceIcon />
                    <TruncatedName title={activeWorkspace?.name}>
                      {activeWorkspace?.name}
                    </TruncatedName>

                    <ChevronDownIcon />
                  </StyledNavLink>

                  <DropdownMenu $align="right">
                    <div>
                      {myWorkspaces.map((ws) => (
                        <StyledNavLink
                          key={ws.id}
                          isActive={ws.active}
                          onClick={() => !ws.active && switchWorkspace(ws.id)}
                        >
                          <WorkspaceIcon />
                          <TruncatedName $maxWidth="14rem" title={ws.name}>
                            {ws.name}
                          </TruncatedName>
                        </StyledNavLink>
                      ))}
                    </div>
                  </DropdownMenu>
                </DropdownWrapper>

                <Divider />
              </>
            )}

            <DropdownWrapper>
              <StyledNavLink isActive={isSettingsActive}>
                <SettingsIcon />
                {t("nav:settings")}
              </StyledNavLink>

              <DropdownMenu $align="right">
                <div>
                  {settingsNav.map((item) =>
                    item.openInNewTab ? (
                      <a
                        key={item.path}
                        href={`/${item.path}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <StyledNavLink isActive={checkIsActive(item.path)}>
                          {item.i18nKey ? t(item.i18nKey) : item.label}
                        </StyledNavLink>
                      </a>
                    ) : (
                      <Link key={item.path} to={item.path}>
                        <StyledNavLink isActive={checkIsActive(item.path)}>
                          {item.i18nKey ? t(item.i18nKey) : item.label}
                        </StyledNavLink>
                      </Link>
                    ),
                  )}
                </div>
              </DropdownMenu>
            </DropdownWrapper>

            <Divider />

            <LanguageToggle />
            <StyleToggle />
            <ThemeToggle />

            <Divider />

            <NotificationBell />
            <SupportWidget />

            <DropdownWrapper>
              <UserName title={user?.username}>
                <UserIcon />
              </UserName>

              <DropdownMenu $align="right">
                <div>
                  {user?.role === "SUPER_ADMIN" && (
                    <Link to="/admin/teams">
                      <AdminNavLink
                        isActive={location.pathname.includes("/admin/")}
                      >
                        <ShieldIcon />
                        {t("nav:admin_panel", "Admin Panel")}
                      </AdminNavLink>
                    </Link>
                  )}

                  <Link to="/account">
                    <StyledNavLink isActive={checkIsActive("account")}>
                      {t("nav:account", "Account")}
                    </StyledNavLink>
                  </Link>

                  <LogoutButton onClick={logout}>
                    <LogoutIcon />
                    {t("nav:logout")}
                  </LogoutButton>
                </div>
              </DropdownMenu>
            </DropdownWrapper>
          </NavGroup>
        </HeaderRight>

        <MobileMenuButton onClick={() => setIsMobileOpen(true)}>
          <MenuIcon />
        </MobileMenuButton>
      </HeaderContainer>

      {/* MOBILE DRAWER */}
      <MobileOverlay
        isOpen={isMobileOpen}
        onClick={() => setIsMobileOpen(false)}
      />

      <MobileDrawer isOpen={isMobileOpen}>
        <DrawerHeader>
          <Logo />
          <MobileMenuButton onClick={() => setIsMobileOpen(false)}>
            <CloseIcon />
          </MobileMenuButton>
        </DrawerHeader>

        <DrawerNavList>
          {myWorkspaces && myWorkspaces.length > 0 && (
            <>
              <DrawerSectionTitle>
                {t("nav:workspace", "Workspace")}
              </DrawerSectionTitle>
              <SettingGroup style={{ marginBottom: "1rem" }}>
                <SettingLabel style={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <WorkspaceIcon />
                  <span style={{ flex: 1 }}>{t("nav:workspace", "Workspace")}</span>

                </SettingLabel>
                <SettingSelect
                  value={activeWorkspace?.id || ""}
                  onChange={(e) => {
                    const newId = e.target.value;
                    if (newId !== activeWorkspace?.id) {
                      switchWorkspace(newId);
                      setIsMobileOpen(false);
                    }
                  }}
                >
                  {myWorkspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.name}
                    </option>
                  ))}
                </SettingSelect>
              </SettingGroup>
            </>
          )}

          <DrawerSectionTitle>{t("nav:main")}</DrawerSectionTitle>
          {mainNav.map((item) => (
            <Link key={item.path} to={item.path}>
              <StyledNavLink isActive={checkIsActive(item.path)}>
                {item.i18nKey ? t(item.i18nKey) : item.label}
              </StyledNavLink>
            </Link>
          ))}

          {campaignsNav.length > 0 && (
            <>
              <DrawerSectionTitle>
                {t("nav:campaigns", "Campaigns")}
              </DrawerSectionTitle>
              {campaignsNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                >
                  <StyledNavLink isActive={checkIsActive(item.path)}>
                    {item.i18nKey ? t(item.i18nKey) : item.label}
                  </StyledNavLink>
                </Link>
              ))}
            </>
          )}

          {assetsNav.length > 0 && (
            <>
              <DrawerSectionTitle>
                {t("nav:traffic_assets", "Traffic Assets")}
              </DrawerSectionTitle>
              {assetsNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                >
                  <StyledNavLink isActive={checkIsActive(item.path)}>
                    {item.i18nKey ? t(item.i18nKey) : item.label}
                  </StyledNavLink>
                </Link>
              ))}
            </>
          )}

          {analyticsNav.length > 0 && (
            <>
              <DrawerSectionTitle>
                {t("nav:analytics", "Analytics")}
              </DrawerSectionTitle>
              {analyticsNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                >
                  <StyledNavLink isActive={checkIsActive(item.path)}>
                    {item.i18nKey ? t(item.i18nKey) : item.label}
                  </StyledNavLink>
                </Link>
              ))}
            </>
          )}

          <DrawerSectionTitle>{t("nav:settings")}</DrawerSectionTitle>
          {settingsNav.map((item) =>
            item.openInNewTab ? (
              <a
                key={item.path}
                href={`/${item.path}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => setIsMobileOpen(false)}
                style={{ textDecoration: "none" }}
              >
                <StyledNavLink isActive={checkIsActive(item.path)}>
                  {item.i18nKey ? t(item.i18nKey) : item.label}
                </StyledNavLink>
              </a>
            ) : (
              <Link key={item.path} to={item.path}>
                <StyledNavLink isActive={checkIsActive(item.path)}>
                  {item.i18nKey ? t(item.i18nKey) : item.label}
                </StyledNavLink>
              </Link>
            ),
          )}

          <DrawerSectionTitle>{t("nav:appearance")}</DrawerSectionTitle>
          <AppearanceSettings />

          <DrawerSectionTitle>{t("nav:account")}</DrawerSectionTitle>
          <DrawerAccount>
            <NotificationBell showLabel={true} />
          </DrawerAccount>
          <DrawerAccount>
            <SupportWidget showLabel={true} />
          </DrawerAccount>
          <DrawerAccount>
            <Link to="/account" onClick={() => setIsMobileOpen(false)}>
              <UserName title={user?.username}>
                <UserIcon />
                <TruncatedName $maxWidth="14rem">
                  {user?.username}
                </TruncatedName>
              </UserName>
            </Link>
          </DrawerAccount>
          {user?.role === "SUPER_ADMIN" && (
            <DrawerAccount>
              <Link to="/admin/teams" onClick={() => setIsMobileOpen(false)}>
                <AdminNavLink isActive={location.pathname.includes("/admin/")}>
                  <ShieldIcon />
                  {t("nav:admin_panel", "Admin Panel")}
                </AdminNavLink>
              </Link>
            </DrawerAccount>
          )}
          <LogoutButton onClick={logout}>
            <LogoutIcon />
            {t("nav:logout")}
          </LogoutButton>
        </DrawerNavList>
      </MobileDrawer>
    </>
  );
};
